import {
    useClickOutside,
} from "@mrflark/core/hooks";
import {
    ChevronDown,
    LogOut,
    PanelLeft,
    User,
} from "lucide-preact";
import type {
    ReactElement,
} from "preact/compat";
import {
    useMemo,
    useRef,
    useState,
} from "preact/compat";
import {
    Link,
} from "react-router-dom";

import {
    BROWSING_MODE_LABELS,
} from "../../constants.ts";
import {
    useAuthStore,
} from "../../stores/authStore.ts";
import {
    useViewerStore,
} from "../../stores/viewerStore.ts";
import {
    Tooltip,
} from "../common/Tooltip.tsx";

type ViewerHeaderProps = {
    onToggleLeftSidebar: () => void;
};

export const ViewerHeader = ({
    onToggleLeftSidebar,
}: ViewerHeaderProps): ReactElement => {
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    useClickOutside([menuRef, buttonRef], () => setUserMenuOpen(false));

    const {
        userId,
        username,
        clearAuth,
        canManage,
    } = useAuthStore();

    const {
        browsingMode,
        orderedIndex,
        orderedTotalCount,
        totalFound,
        isSearchActive,
        searchQuery,
        searchResultsIndex,
        searchTotalCount,
        filters,
    } = useViewerStore();

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (filters.media_type && filters.media_type !== "all") count++;
        if (filters.has_watermark) count++;
        if (filters.tag_query?.trim()) count++;
        return count;
    }, [filters]);

    const statusText = useMemo(() => {
        if (isSearchActive && searchTotalCount > 0) {
            const truncatedQuery = searchQuery.length > 20
                ? searchQuery.slice(0, 20) + "..."
                : searchQuery;
            return `Search: "${truncatedQuery}" · ${
                searchResultsIndex + 1
            } of ${searchTotalCount}`;
        }
        if (browsingMode === "random") {
            const countDisplay = totalFound > 0
                ? ` · ~${totalFound.toLocaleString()} items`
                : "";
            return `${BROWSING_MODE_LABELS[browsingMode]}${countDisplay}`;
        }
        if (orderedTotalCount > 0) {
            return `${BROWSING_MODE_LABELS[browsingMode]} · ${
                orderedIndex + 1
            } of ${orderedTotalCount.toLocaleString()}`;
        }
        return BROWSING_MODE_LABELS[browsingMode];
    }, [
        browsingMode,
        totalFound,
        orderedIndex,
        orderedTotalCount,
        isSearchActive,
        searchQuery,
        searchResultsIndex,
        searchTotalCount,
    ]);

    const handleLogout = (): void => {
        clearAuth();
        globalThis.location.href = "https://auth.mrflark.org/logout";
    };

    return (
        <header className="fixed top-0 left-0 right-0 h-14 bg-bg-secondary/80 backdrop-blur-md border-b border-border z-30 flex items-center justify-between px-4">
            <div className="flex items-center gap-3">
                <h1 className="text-lg font-semibold">SPL</h1>
            </div>

            <div className="flex items-center gap-1 text-sm text-text-secondary">
                <span>{statusText}</span>
            </div>

            <div className="flex items-center gap-2">
                <Tooltip
                    content="Toggle sidebar. Shortcut: ["
                    position="bottom"
                >
                    <button
                        type="button"
                        onClick={onToggleLeftSidebar}
                        className="relative p-2 text-text-secondary hover:text-text-primary transition-colors"
                    >
                        <PanelLeft size={20} />
                        {activeFilterCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 flex items-center justify-center text-[10px] font-medium bg-accent text-white rounded-full leading-none">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                </Tooltip>

                {canManage() && (
                    <Link
                        to="/manage"
                        className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
                    >
                        Manage
                    </Link>
                )}

                {userId && (
                    <Link
                        to={`/profile/${userId}`}
                        className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
                    >
                        Profile
                    </Link>
                )}

                <div className="relative">
                    <button
                        type="button"
                        ref={buttonRef}
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors rounded-md hover:bg-bg-tertiary"
                    >
                        <User size={16} />
                        <span>{username}</span>
                        <ChevronDown
                            size={14}
                            className={`transition-transform ${
                                userMenuOpen ? "rotate-180" : ""
                            }`}
                        />
                    </button>

                    {userMenuOpen && (
                        <div
                            ref={menuRef}
                            className="absolute right-0 top-full mt-1 w-40 bg-bg-secondary border border-border rounded-lg shadow-lg overflow-hidden z-50"
                        >
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-text-secondary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};
